/**
 * Lista de análises — cards ricos com funil visual.
 *
 * Substitui a tabela bruta por uma grade de cards inspirada no ProjetoLista.
 * Cada card mostra: título, setor, executor, badge de estado amigável,
 * score (verde/amarelo/cinza), barra do funil (etapa atual no fluxo) e
 * tempo relativo.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Plus, ArrowRight, Filter } from 'lucide-react';
import { analisesAPI } from '../../services/inovacaoAPI';
import {
  rotuloEstadoAnalise, ROTULO_ESTADO_ANALISE, ETAPAS_FUNIL_ANALISE,
} from './labels';

const BRAND = 'var(--brand, #20a490)';

const FILTROS = [
  { chave: '',                      label: 'Todas' },
  { chave: 'rascunho',              label: 'Rascunhos' },
  { chave: 'aguardando_respostas',  label: 'Pendentes' },
  { chave: 'em_revisao_gni',        label: 'Revisão' },
  { chave: 'em_aprovacao',          label: 'Em aprovação' },
  { chave: 'aprovada',              label: 'Aprovadas' },
  { chave: 'rejeitada',             label: 'Rejeitadas' },
  { chave: 'backlog',               label: 'Backlog' },
];

export default function AnaliseLista() {
  const [items, setItems] = useState([]);
  const [filtros, setFiltros] = useState({ estado: '', busca: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { size: 60 };
    if (filtros.estado) params.estado = filtros.estado;
    if (filtros.busca) params.busca = filtros.busca;
    analisesAPI.listar(params)
      .then((res) => setItems(res.items))
      .catch((e) => toast.error(e.response?.data?.detail || 'Erro ao listar'))
      .finally(() => setLoading(false));
  }, [filtros.estado, filtros.busca]);

  const contadores = useMemo(() => {
    const c = {};
    items.forEach((a) => { c[a.estado] = (c[a.estado] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <div style={{ padding: 32, background: '#f5f7fa', minHeight: '100vh' }}>
      {/* Cabeçalho */}
      <div style={estilos.cabecalho}>
        <div>
          <h1 style={{ margin: 0, color: '#2c3e50' }}>Análises de Oportunidade</h1>
          <p style={{ margin: '4px 0 0', color: '#7f8c8d' }}>
            {items.length} análise{items.length !== 1 ? 's' : ''} no filtro atual
          </p>
        </div>
        <Link to="/inovacao/analises/nova" style={estilos.btnNova}>
          <Plus size={16} /> Nova análise
        </Link>
      </div>

      {/* Filtros + busca */}
      <div style={estilos.barraFiltros}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {FILTROS.map((f) => {
            const ativo = filtros.estado === f.chave;
            const cor = f.chave ? rotuloEstadoAnalise(f.chave).cor : BRAND;
            return (
              <button key={f.chave}
                      onClick={() => setFiltros({ ...filtros, estado: f.chave })}
                      style={{
                        padding: '6px 14px', fontSize: '0.82rem', fontWeight: 500,
                        background: ativo ? cor : '#fff',
                        color: ativo ? '#fff' : '#555',
                        border: `1px solid ${ativo ? cor : '#ddd'}`,
                        borderRadius: 18, cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                      }}>
                {f.label}
                {f.chave && contadores[f.chave] != null && (
                  <span style={{
                    background: ativo ? 'rgba(255,255,255,0.25)' : '#ecf0f1',
                    color: ativo ? '#fff' : '#7f8c8d',
                    padding: '0 6px', borderRadius: 8, fontSize: '0.7rem',
                  }}>{contadores[f.chave]}</span>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ position: 'relative', minWidth: 240 }}>
          <input placeholder="Buscar por título…"
                 value={filtros.busca}
                 onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })}
                 style={{
                   width: '100%', padding: '8px 12px 8px 32px',
                   borderRadius: 6, border: '1px solid #ddd', fontSize: '0.85rem',
                 }} />
          <Filter size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#95a5a6' }} />
        </div>
      </div>

      {/* Grade de cards */}
      {loading ? (
        <p style={{ color: '#7f8c8d' }}>Carregando…</p>
      ) : items.length === 0 ? (
        <div style={estilos.vazio}>
          <p style={{ color: '#bdc3c7', margin: 0 }}>
            Nenhuma análise encontrada com os filtros atuais.
          </p>
          <Link to="/inovacao/analises/nova"
                style={{ ...estilos.btnNova, marginTop: 16, display: 'inline-flex' }}>
            <Plus size={16} /> Criar primeira análise
          </Link>
        </div>
      ) : (
        <div style={estilos.grade}>
          {items.map((a) => <CardAnalise key={a.id} a={a} />)}
        </div>
      )}
    </div>
  );
}

function CardAnalise({ a }) {
  const info = rotuloEstadoAnalise(a.estado);
  const corScore = a.score_oportunidade == null ? '#95a5a6'
                 : a.score_oportunidade >= 70 ? '#27ae60'
                 : a.score_oportunidade >= 40 ? '#f39c12'
                 : '#e74c3c';

  return (
    <Link to={`/inovacao/analises/${a.id}`}
          style={estilos.card}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
          }}>
      <div style={{ borderLeft: `4px solid ${info.cor}`, paddingLeft: 14, marginLeft: -18 }}>
        {/* Cabeçalho do card */}
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: '1rem', color: '#2c3e50',
                       lineHeight: 1.3, flex: 1 }}>
            {a.titulo}
          </h3>
          {a.score_oportunidade != null && (
            <div style={{
              background: corScore, color: '#fff',
              padding: '4px 10px', borderRadius: 12,
              fontSize: '0.78rem', fontWeight: 600,
              flexShrink: 0,
            }} title="Score de oportunidade">
              {a.score_oportunidade}
            </div>
          )}
        </div>

        {/* Meta */}
        <div style={{ color: '#7f8c8d', fontSize: '0.78rem', marginBottom: 12 }}>
          {a.setor_solicitante?.nome}
          <span style={{ margin: '0 6px' }}>·</span>
          {a.executor_gni?.nome}
        </div>

        {/* Badge de estado */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: `${info.cor}15`, color: info.cor,
                      padding: '4px 10px', borderRadius: 6,
                      fontSize: '0.78rem', fontWeight: 600, marginBottom: 14 }}>
          {info.label}
        </div>

        {/* Funil visual */}
        {info.etapa >= 0 && <FunilEtapas etapaAtual={info.etapa} />}
        {info.etapa < 0 && <BadgeFora info={info} />}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginTop: 12,
                      paddingTop: 10, borderTop: '1px solid #ecf0f1' }}>
          <span style={{ color: '#95a5a6', fontSize: '0.72rem' }}>
            {tempoRelativo(a.created_at)}
          </span>
          <span style={{ color: BRAND, fontSize: '0.78rem',
                         display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            abrir <ArrowRight size={12} />
          </span>
        </div>
      </div>
    </Link>
  );
}

function FunilEtapas({ etapaAtual }) {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {ETAPAS_FUNIL_ANALISE.map((etapa, i) => {
        const concluida = i < etapaAtual;
        const atual = i === etapaAtual;
        const cor = concluida ? '#27ae60' : atual ? '#3498db' : '#ecf0f1';
        return (
          <React.Fragment key={etapa.chave}>
            <div title={etapa.label} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: cor,
              transition: 'background 0.2s',
            }} />
          </React.Fragment>
        );
      })}
    </div>
  );
}

function BadgeFora({ info }) {
  return (
    <div style={{
      display: 'inline-block', padding: '3px 10px',
      background: `${info.cor}15`, color: info.cor,
      borderRadius: 4, fontSize: '0.72rem', fontWeight: 500,
    }}>
      Fora do funil principal
    </div>
  );
}

function tempoRelativo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const agora = new Date();
  const min = Math.floor((agora - d) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const dias = Math.floor(h / 24);
  if (dias < 30) return `há ${dias}d`;
  return d.toLocaleDateString('pt-BR');
}

const estilos = {
  cabecalho: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 20,
  },
  btnNova: {
    background: BRAND, color: '#fff', padding: '10px 18px',
    textDecoration: 'none', borderRadius: 6, fontWeight: 500,
    display: 'inline-flex', alignItems: 'center', gap: 6,
  },
  barraFiltros: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap',
  },
  grade: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: 16,
  },
  card: {
    background: '#fff', padding: '14px 18px',
    borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    textDecoration: 'none', color: 'inherit',
    transition: 'transform 0.15s, box-shadow 0.15s',
    display: 'block',
  },
  vazio: {
    background: '#fff', padding: 60, borderRadius: 12, textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
};
